import { CardHeader } from "@material-ui/core";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { auth } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  CreateButton,
  Datagrid,
  ExportButton,
  List,
  NumberField,
  ReferenceField,
  TextField,
  TopToolbar,
  useDataProvider,
  ImageField,
} from "react-admin";
import { useClientHas } from "../Machines/RefreshMachineStatus";
import SearchInput from "../SearchInput";
import _extendLayout from "../../layout/_extendLayout";
import ProActionMenu from "./ProActionMenu";
import BulkDeleteButton from "../BulkDeleteButton";

const exporter = async (datas) => {
  const headers = [
    "ID",
    "Category",
    "Client",
    "Created At",
    "Limitation Rule",
    "MRO Access Group",
    "Name",
    "Price Cash",
    "Price Credit",
    "Updated At",
    "Description",
    "Image",
    "Serial Number",
    "SKU",
  ];

  let csvRows = [headers.join(",")];

  datas.forEach((e) => {
    const formattedCreatedAt = e.createdAt?.toDate
      ? `"${e.createdAt.toDate().toLocaleString()}"`
      : e.createdAt?.seconds
        ? `"${new Date(e.createdAt.seconds * 1000).toLocaleString()}"`
        : '""';

    const formattedUpdatedAt = e.updatedAt?.toDate
      ? `"${e.updatedAt.toDate().toLocaleString()}"`
      : e.updatedAt?.seconds
        ? `"${new Date(e.updatedAt.seconds * 1000).toLocaleString()}"`
        : '""';

    const commonDetails = [
      e.id,
      e.category ?? "",
      e.client ?? "",
      formattedCreatedAt,
      e.limitationRule ?? "",
      `"${e.mroAccessGroup?.join(", ") ?? ""}"`,
      `"${e.name}"` ?? "",
      e.priceCash ?? "",
      e.priceCredit ?? "",
      formattedUpdatedAt,
      e.description ?? "",
      e.image?.src ?? "",
      e.serialNumber ?? "",
      e.sku ?? "",
    ];
    csvRows.push([...commonDetails].join(","));
  });

  const csvContent = csvRows.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "products.csv";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const CustomActions = ({ searchInput, ...props }) => (
  <TopToolbar
    style={{
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        flex: 1,
      }}
    >
      {searchInput}
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <CreateButton label="Create" />
      <ExportButton label="Export" exporter={exporter} />
    </div>
  </TopToolbar>
);

const AdminList = ({ ...props }) => (
  <Datagrid
    {...props}
    bulkActionButtons={<BulkDeleteButton resource="products" />}
    sx={{
      minWidth: "800px",
      "& .RaDatagrid-headerCell": { fontWeight: "bold" },
      "& .RaDatagrid-headerCell:nth-of-type(1), & .RaDatagrid-rowCell:nth-of-type(1)":
        {
          width: "6px",
        },
      "& .RaDatagrid-headerCell:nth-of-type(2), & .RaDatagrid-rowCell:nth-of-type(2)":
        {
          width: "60px",
          maxWidth: "60px",
          padding: 0,
        },
      "& .RaDatagrid-headerCell:nth-of-type(3), & .RaDatagrid-rowCell:nth-of-type(3)":
        {
          width: "120px",
        },
      "& .RaDatagrid-headerCell:nth-of-type(4), & .RaDatagrid-rowCell:nth-of-type(4)":
        {
          width: "120px",
        },
      "& .RaDatagrid-headerCell:nth-of-type(5), & .RaDatagrid-rowCell:nth-of-type(5)":
        {
          width: "120px",
        },
      "& .RaDatagrid-headerCell:nth-of-type(6), & .RaDatagrid-rowCell:nth-of-type(6)":
        {
          width: "120px",
        },
      "& .RaDatagrid-headerCell:nth-of-type(7), & .RaDatagrid-rowCell:nth-of-type(7)":
        {
          width: "130px",
        },
      "& .RaDatagrid-headerCell:nth-of-type(8), & .RaDatagrid-rowCell:nth-of-type(8)":
        {
          width: "120px",
        },
      "& .RaDatagrid-headerCell:nth-of-type(9), & .RaDatagrid-rowCell:nth-of-type(9)":
        {
          width: "120px",
        },
      "& .RaDatagrid-headerCell:nth-of-type(10), & .RaDatagrid-rowCell:nth-of-type(10)":
        {
          width: "120px",
        },
    }}
  >
    <ImageField
      source="image.src"
      label="Image"
      sx={{
        "& .RaImageField-image": {
          width: "50px",
          height: "50px",
          objectFit: "contain",
          padding: "1.6px",
          overflow: "hidden",
          margin: "4px 0px",
        },
        "& img": {
          width: "50px",
          height: "50px",
          objectFit: "contain",
          borderRadius: "4px",
          padding: "1.6px",
          overflow: "hidden",
          margin: "4px 0px",
        },
      }}
    />
    <TextField source="name" label="Name" sx={{ width: "200px" }} />
    <ReferenceField
      label="Company"
      source="client"
      reference="clients"
      link={false}
    >
      <TextField source="companyName" />
    </ReferenceField>
    <TextField
      source="description"
      label="Description"
      sx={{ width: "200px" }}
    />
    <TextField source="sku" label="SKU" />
    <NumberField
      source="priceCash"
      label="Price"
      locales="en-MY"
      options={{ style: "currency", currency: "MYR" }}
    />
    <NumberField source="priceCredit" label="Price (Credit)" />
    <ProActionMenu label="Actions" />
  </Datagrid>
);

const ClientList = ({ clientId, ...props }) => {
  const hasMRO = useClientHas(clientId, "mro");

  return (
    <Datagrid
      {...props}
      bulkActionButtons={<BulkDeleteButton resource="products" />}
      sx={{
        minWidth: "800px",
        "& .RaDatagrid-headerCell": { fontWeight: "bold" },
        "& .RaDatagrid-headerCell:nth-of-type(1), & .RaDatagrid-rowCell:nth-of-type(1)":
          {
            width: "6px",
          },
        "& .RaDatagrid-headerCell:nth-of-type(2), & .RaDatagrid-rowCell:nth-of-type(2)":
          {
            width: "60px",
            maxWidth: "60px",
            padding: 0,
          },
        "& .RaDatagrid-headerCell:nth-of-type(3), & .RaDatagrid-rowCell:nth-of-type(3)":
          {
            width: "120px",
          },
        "& .RaDatagrid-headerCell:nth-of-type(4), & .RaDatagrid-rowCell:nth-of-type(4)":
          {
            width: "120px",
          },
        "& .RaDatagrid-headerCell:nth-of-type(5), & .RaDatagrid-rowCell:nth-of-type(5)":
          {
            width: "120px",
          },
        "& .RaDatagrid-headerCell:nth-of-type(6), & .RaDatagrid-rowCell:nth-of-type(6)":
          {
            width: "120px",
          },
        "& .RaDatagrid-headerCell:nth-of-type(7), & .RaDatagrid-rowCell:nth-of-type(7)":
          {
            width: "130px",
          },
        "& .RaDatagrid-headerCell:nth-of-type(8), & .RaDatagrid-rowCell:nth-of-type(8)":
          {
            width: "120px",
          },
        "& .RaDatagrid-headerCell:nth-of-type(9), & .RaDatagrid-rowCell:nth-of-type(9)":
          {
            width: "120px",
          },
        "& .RaDatagrid-headerCell:nth-of-type(10), & .RaDatagrid-rowCell:nth-of-type(10)":
          {
            width: "120px",
          },
      }}
    >
      <ImageField
        source="image.src"
        label="Image"
        sx={{
          "& .RaImageField-image": {
            width: "50px",
            height: "50px",
            objectFit: "contain",
            padding: "1.6px",
            overflow: "hidden",
            margin: "4px 0px",
          },
          "& img": {
            width: "50px",
            height: "50px",
            objectFit: "contain",
            borderRadius: "4px",
            padding: "1.6px",
            overflow: "hidden",
            margin: "4px 0px",
          },
        }}
      />
      <TextField source="name" label="Name" sx={{ width: "200px" }} />
      <TextField
        source="description"
        label="Description"
        sx={{ width: "200px" }}
      />
      {hasMRO && <TextField source="sku" label="SKU" />}
      <NumberField
        source="priceCash"
        label="Price"
        locales="en-MY"
        options={{ style: "currency", currency: "MYR" }}
      />
      {hasMRO && <NumberField source="priceCredit" label="Price (Credit)" />}
      <ProActionMenu label="Actions" />
    </Datagrid>
  );
};

const ProductList = (props) => {
  const dataProvider = useDataProvider();

  const [isAdmin, setIsAdmin] = useState(false);
  const [clientId, setClientId] = useState(null);

  useEffect(() => {
    const checkAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        await getCurrentUser(user.uid);
      }
    });
    return () => checkAuth();
  }, []);

  const getCurrentUser = async (userId) => {
    try {
      const res = await dataProvider.getOne("users", { id: userId });
      setIsAdmin(res.data.role === "admin");
      setClientId(res.data?.client?.id ?? null);
    } catch (err) {
      console.log("User not found ", err);
    }
  };

  const getProductList = async ({ queryKey }) => {
    const token = localStorage.getItem("token");
    const [, , { filter, pagination, sort }] = queryKey;
    const { q, ...restFilter } = filter || {};
    const search = q?.trim();
    const params = {
      ...restFilter,
      ...(search ? { search } : {}),
      skip: (pagination.page - 1) * pagination.perPage,
      limit: pagination.perPage,
      sortField: sort.field,
      sortOrder: sort.order,
    };

    const res = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/product/inventoryProduct`,
      {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );

    const newFilteredData = res.data.result.data;
    if (Array.isArray(newFilteredData)) {
      const formattedData = newFilteredData.map((item) => ({
        ...item,
        id: item.id,
        image: {
          src: item.image?.src ?? "",
        },
      }));
      return {
        data: formattedData,
        total: res.data.result.total,
      };
    }
  };

  // same as CategoryList
  const filter = clientId ? { client: clientId } : { admin: isAdmin };

  const searchFilters = <SearchInput searchKeys={["name"]} />;

  return (
    <>
      <_extendLayout>
        <div style={{ width: "90%" }}>
          <CardHeader title="List Of Product" />

          <List
            {...props}
            key={clientId || "all"}
            resource="products"
            actions={<CustomActions />}
            filter={filter}
            filters={searchFilters}
            sort={{ field: "name", order: "ASC" }}
            perPage={25}
            empty={false}
            queryOptions={{ queryFn: getProductList }}
          >
            {clientId ? <ClientList clientId={clientId} /> : <AdminList />}
          </List>
        </div>
      </_extendLayout>
    </>
  );
};

export default ProductList;
